import ButtonUI from './ButtonUI';
import { onCTAClick } from '../../../../lib/atm';
import { DATA_TRACK_TYPE_BANNER } from '@lib/constants';

/**
 * Button Component
 *
 * A higher-order component that wraps `ButtonUI` and provides additional functionality,
 * including analytics tracking and dynamic ID generation. This component supports
 * accessibility, data tracking, and customization through props.
 *
 * @component
 * @param {Object} props - The properties passed to the component.
 * @param {string} [props.url] - The URL to navigate to when the button is clicked, rendering it as a link.
 * @param {Function} [props.onClick] - The function to call when the button is clicked.
 * Analytics tracking logic is also triggered.
 * @param {string} [props.dataContextContainer] - The context container ID for tracking purposes.
 * @param {string} [props.dataLayerId] - The layer ID for analytics or tracking purposes.
 * @param {string} [props.dataTrackingId] - The unique identifier for tracking button interactions.
 * @param {string} [props.dataTrack] -
 * @param {string} [props.dataTrackType] - The type of tracking applied to the button.
 * @param {Object} [props.bannerTracking] - Semantic Adobe banner tracking values.
 * @param {string} [props.idPrefix] - A prefix added to the generated ID for the button.
 * @param {string} [props.ariaLabel] - An ARIA label for accessibility, linking it to another element.
 * @param {string} [props.accessibleLabel] - A custom accessible label describing the button's functionality.
 * @param {React.ReactNode} [props.children] - The content to display inside the button.
 * @param {Object} [props.rest] - Additional props to spread onto the `ButtonUI` component.
 *
 * @example
 * // Example usage with tracking and analytics
 * <Button
 *   variant="primary"
 *   theme="light"
 *   size="medium"
 *   buttonText="Home"
 *   url="https://example.com"
 *   onClick={() => console.log('Button clicked')}
 *   dataContextContainer="main-container"
 *   dataLayerId="layer-1"
 *   dataTrackingId="button-123"
 *   dataTrack='data-track-value'
 *   dataTrackType="cta"
 *   idPrefix="home"
 *   ariaLabel="button-home"
 *   accessibleLabel="Go to home"
 * />
 *
 * @returns {JSX.Element} The rendered Button component.
 */
const Button = (props) => {
  const {
    url: href,
    onClick,
    dataContextContainer,
    dataLayerId,
    dataTrackingId,
    dataTrack,
    dataTrackType,
    bannerTracking,
    idPrefix,
    ariaLabel,
    accessibleLabel,
    children,
    ...rest
  } = props;

  const id = idPrefix
    ? `${idPrefix}_${dataTrackingId}`
    : dataTrackingId;

  const bannerDataAttributes = bannerTracking
    ? {
      trackingId: bannerTracking.linkName,
      trackClick: bannerTracking.bannerCategory,
      trackType: DATA_TRACK_TYPE_BANNER,
      trackLocation: bannerTracking.bannerLocation,
      trackBannerName: bannerTracking.bannerName,
    }
    : {
      trackingId: dataTrackingId,
      trackClick: dataTrack,
      trackType: dataTrackType,
    };

  const onClickWithAnalytics = (e) => {
    if (e) {
      onCTAClick(e);
      e.stopPropagation();
    }

    onClick && onClick();
  };

  return (
    <ButtonUI
      id={id}
      href={href}
      onClick={onClickWithAnalytics}
      data-track-id={bannerDataAttributes.trackingId}
      data-track-click={bannerDataAttributes.trackClick}
      data-track-type={bannerDataAttributes.trackType}
      data-track-location={bannerDataAttributes.trackLocation}
      data-track-banner-name={bannerDataAttributes.trackBannerName}
      data-context-container={dataContextContainer}
      data-layer-source-id={dataLayerId}
      aria-labelledby={ariaLabel}
      aria-label={accessibleLabel}
      {...rest}
    >
      {children}
    </ButtonUI>
  );
};

export default Button;
